import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { createFrontLayout } from "@/layout/front/front";
import { createSectionSupport } from "@/components/front/section-support/section-support";
import { createSubMenu } from "@/components/front/sub-menu/sub-menu";
import { createYoutubeDialog } from "@/components/front/youtube-dialog/youtube-dialog";
import { UrlParser } from "@/utils/attribute-parser/url-parser";
import { SetToggle } from "@/utils/dom";
import { addTranslation } from "@/utils/i18n";
import { zodUnsafeValidate } from "@/utils/zod";
import { InstagramSearchApp } from "./components/instagram-search-app/InstagramSearchApp";
import { useInstagramSearch } from "./hooks/useInstagramSearch";
import { useInstagramSearchForm } from "./hooks/useInstagramSearchForm";
import { InstagramSearch } from "./types";
import "./instagram-search.css";

createFrontLayout();
createSubMenu();
createYoutubeDialog();
createSectionSupport();

const container = document.querySelector(".instagram-search-app");

if (container !== null && window.instagramSearchConfig !== undefined) {
  const config = zodUnsafeValidate(
    InstagramSearch.ConfigSchema,
    window.instagramSearchConfig,
  );

  addTranslation(config.translation);

  const initial = {
    ...useInstagramSearchForm.getState().initial,

    followers: {
      start: config.followers.min,
      end: config.followers.max,
    },

    posts: {
      start: config.posts.min,
      end: config.posts.max,
    },
  };

  useInstagramSearch.setState({
    config: config,
    isAuthorized: config.isAuthorized,
    isSubscribed: config.isSubscribed,
    followers: initial.followers,
    posts: initial.posts,
  });

  const url = new UrlParser(document.location.href);
  const searchParams = new URLSearchParams(document.location.search);

  const isInitiallySubmitted =
    searchParams.has("profile") === false &&
    searchParams.has("category") === false &&
    searchParams.has("country") === false &&
    searchParams.has("region") === false &&
    searchParams.has("city") === false &&
    searchParams.has("gender") === false &&
    searchParams.has("followersStart") === false &&
    searchParams.has("followersEnd") === false &&
    searchParams.has("postsStart") === false &&
    searchParams.has("postsEnd") === false;

  useInstagramSearchForm.setState({
    query: url.string("profile", ""),
    category: url.number("category"),
    country: url.number("country"),
    regions: new SetToggle(url.numberArray("regions")),
    cities: new SetToggle(url.numberArray("cities")),
    gender: url.number("gender"),
    followers: {
      start: url.number("followersStart", config.followers.min),
      end: url.number("followersEnd", config.followers.max),
    },
    posts: {
      start: url.number("postsStart", config.posts.min),
      end: url.number("postsEnd", config.posts.max),
    },
    initial,
    isInitiallySubmitted: isInitiallySubmitted,
  });

  const root = createRoot(container);

  root.render(
    <BrowserRouter basename={config.router.basename}>
      <InstagramSearchApp />
    </BrowserRouter>,
  );
}
